import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Calculator, TrendingUp, DollarSign } from 'lucide-react';

const ROICalculator = () => {
  const [investmentAmount, setInvestmentAmount] = useState(15000);
  const [selectedRoi, setSelectedRoi] = useState(15);
  const [compoundMode, setCompoundMode] = useState(true);

  const roiOptions = [
    { label: 'Starter (12%)', value: 12 },
    { label: 'Growth (15%)', value: 15 },
    { label: 'Enterprise (18%)', value: 18 },
  ];

  const projections = useMemo(() => {
    const principal = Number(investmentAmount) || 0;
    const rate = selectedRoi / 100;

    if (compoundMode) {
      const y1 = principal * Math.pow(1 + rate, 1);
      const y2 = principal * Math.pow(1 + rate, 2);
      const y3 = principal * Math.pow(1 + rate, 3);
      return [
        { year: 'Year 1', total: y1, earnings: y1 - principal, principal },
        { year: 'Year 2', total: y2, earnings: y2 - principal, principal },
        { year: 'Year 3', total: y3, earnings: y3 - principal, principal },
      ];
    } else {
      const annualReturn = principal * rate;
      return [
        { year: 'Year 1', total: principal + annualReturn, earnings: annualReturn, principal },
        { year: 'Year 2', total: principal + annualReturn * 2, earnings: annualReturn * 2, principal },
        { year: 'Year 3', total: principal + annualReturn * 3, earnings: annualReturn * 3, principal },
      ];
    }
  }, [investmentAmount, selectedRoi, compoundMode]);

  const formatCurrency = (val) =>
    new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 })?.format(val);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-[#1a2744] text-white rounded-xl p-3 shadow-xl text-xs">
          <p className="font-bold mb-1">{label}</p>
          <p>Total: <span className="text-[#c9a84c]">{formatCurrency(payload?.[0]?.payload?.total)}</span></p>
          <p>Earnings: <span className="text-green-400">{formatCurrency(payload?.[0]?.payload?.earnings)}</span></p>
        </div>
      );
    }
    return null;
  };

  return (
    <section id="calculator" className="mb-16">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-[#1a2744] mb-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          ROI Calculator
        </h2>
        <p className="text-slate-500 text-sm">Visualize your projected returns over 3 years</p>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-5">
          {/* Controls */}
          <div className="lg:col-span-2 bg-[#1a2744] p-8">
            <div className="flex items-center gap-2 mb-6">
              <Calculator size={20} className="text-[#c9a84c]" />
              <span className="text-white font-semibold">Configure Investment</span>
            </div>

            <div className="space-y-6">
              <div>
                <label className="text-white/70 text-xs mb-2 block">Investment Amount (KES)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#c9a84c] font-bold text-sm">KES</span>
                  <input
                    type="number"
                    value={investmentAmount}
                    onChange={(e) => setInvestmentAmount(Math.max(0, Number(e?.target?.value)))}
                    className="w-full bg-white/10 text-white border border-white/20 rounded-xl pl-12 pr-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c] focus:ring-1 focus:ring-[#c9a84c]"
                    min="0"
                    step="1000"
                  />
                </div>
                <input
                  type="range"
                  min="5000"
                  max="100000"
                  step="1000"
                  value={investmentAmount}
                  onChange={(e) => setInvestmentAmount(Number(e?.target?.value))}
                  className="w-full mt-3 accent-[#c9a84c]"
                />
                <div className="flex justify-between text-white/40 text-xs mt-1">
                  <span>KES 5K</span>
                  <span>KES 100K</span>
                </div>
              </div>

              <div>
                <label className="text-white/70 text-xs mb-2 block">Investment Tier</label>
                <div className="space-y-2">
                  {roiOptions?.map((opt) => (
                    <button
                      key={opt?.value}
                      onClick={() => setSelectedRoi(opt?.value)}
                      className={`w-full text-left px-4 py-2.5 rounded-xl text-sm transition-all ${
                        selectedRoi === opt?.value
                          ? 'bg-[#c9a84c] text-[#1a2744] font-semibold'
                          : 'bg-white/10 text-white/80 hover:bg-white/20'
                      }`}
                    >
                      {opt?.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-white/70 text-xs mb-2 block">Growth Mode</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCompoundMode(true)}
                    className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all ${
                      compoundMode ? 'bg-[#c9a84c] text-[#1a2744]' : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    Compound
                  </button>
                  <button
                    onClick={() => setCompoundMode(false)}
                    className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all ${
                      !compoundMode ? 'bg-[#c9a84c] text-[#1a2744]' : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    Simple
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Chart & Results */}
          <div className="lg:col-span-3 p-8">
            <div className="grid grid-cols-3 gap-4 mb-8">
              {projections?.map((p, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-4 text-center">
                  <div className="text-xs text-slate-400 mb-1">{p?.year}</div>
                  <div className="text-[#1a2744] font-bold text-base" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    {formatCurrency(p?.total)}
                  </div>
                  <div className="text-green-600 text-xs font-medium mt-1 flex items-center justify-center gap-1">
                    <TrendingUp size={10} />
                    +{formatCurrency(p?.earnings)}
                  </div>
                </div>
              ))}
            </div>

            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={projections} barSize={40}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${(v / 1000)?.toFixed(0)}K`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                    {projections?.map((_, i) => (
                      <Cell key={i} fill={i === 0 ? '#1a2744' : i === 1 ? '#c9a84c' : '#8B6914'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-100 flex items-start gap-3">
              <DollarSign size={16} className="text-[#c9a84c] mt-0.5 flex-shrink-0" />
              <p className="text-xs text-slate-600">
                <span className="font-semibold text-[#1a2744]">3-Year Total: {formatCurrency(projections?.[2]?.total)}</span>
                {' '}— Earning {formatCurrency(projections?.[2]?.earnings)} on your {formatCurrency(Number(investmentAmount))} investment at {selectedRoi}% ROI.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
